/**
 * Marca blanca de los DSP en textos que llegan del backend.
 *
 * El producto nunca nombra a sus DSP delante del usuario: MediaSmart se muestra como
 * "GeoActivate" y Perion/Hivestack se vende como "DOOH Direct". La API todavía reenvía textos con
 * esos nombres (errores del DSP, avisos del import...), así que todo mensaje del backend que se
 * pinte tal cual pasa por aquí antes de llegar a la pantalla.
 *
 * El orden importa: primero se quitan las URLs del DSP (si se sustituyera antes el nombre, la URL
 * quedaría rota pero visible: "https://apps.DOOH Direct.com/..."), después el prefijo técnico
 * "(Perion DSP)" y por último los nombres.
 */

/**
 * Consola, API y portal de los DSP (hivestack.com, mediasmart.io y sus subdominios), con o sin
 * esquema. Se reconocen solo por el HOST y se eliminan enteras: nuestras propias URLs pueden llevar
 * el nombre en la ruta ("https://storage.api.geoactivate.ai/perion-creatives/...") y no se tocan.
 *
 * Sin lookbehind a propósito, aquí y en `URL_OR_DSP_NAME`: Safari/iOS anteriores a 16.4 no lo
 * soportan, y como este fichero va en el bundle principal (lo importa el ErrorInterceptor) un solo
 * lookbehind dejaba la app entera sin cargar en esos dispositivos. El carácter previo se captura
 * en el grupo 1 y se devuelve tal cual.
 */
const DSP_URL = /(^|[^\w.\/-])(?:https?:\/\/)?(?:[\w-]+\.)*(?:hivestack\.com|mediasmart\.io)(?![\w-])(?:[:\/?#]\S*)?/gi;

/** Puntuación de cierre que `\S*` se lleva con la URL pero que pertenece a la frase. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

/** Prefijo técnico con el que algunos mensajes llegan de la API, p. ej. "(Perion DSP) ...". */
const DSP_PREFIX = /^\s*\((?:perion|hivestack|media\s?smart)(?:\s+dsp)?\)\s*/i;

/**
 * Nombres de los DSP, solo cuando no van pegados a otra letra: "PERION_API_KEY" y "(Perion DSP)"
 * pierden el nombre, "Experion" no. Las URLs que quedan (las nuestras) casan antes con las dos
 * primeras alternativas y se devuelven intactas.
 */
const URL_OR_DSP_NAME = /https?:\/\/\S+|\b(?:[\w-]+\.)+[a-z]{2,}\/\S*|(^|[^a-z])(perion|hivestack)(?![a-z])|(^|[^a-z])(media\s?smart)(?![a-z])/gi;

export const WHITE_LABEL_MEDIASMART = "GeoActivate";
export const WHITE_LABEL_PERION = "DOOH Direct";

export function whiteLabelDspText(text: string): string {
    if (typeof text !== "string" || !text) {
        return text;
    }

    let result = text.replace(DSP_URL, (match: string, before: string) => before + (match.slice(before.length).match(TRAILING_PUNCTUATION)?.[0] ?? ""));
    if (result !== text) {
        // Solo si se ha quitado alguna URL: recoloca lo que queda alrededor del hueco.
        result = result
            .replace(/\(\s*\)|\[\s*\]/g, "")
            .replace(/[ \t]+([.,;:!?)\]])/g, "$1")
            .replace(/[ \t]{2,}/g, " ")
            .trim();
    }

    return result
        .replace(DSP_PREFIX, "")
        .replace(URL_OR_DSP_NAME, (match: string, beforePerion?: string, perion?: string, beforeMediasmart?: string, mediasmart?: string) =>
            perion ? beforePerion + WHITE_LABEL_PERION : mediasmart ? beforeMediasmart + WHITE_LABEL_MEDIASMART : match
        );
}
